import { chatStream } from "../request/common";
import { ConfigService } from "../../assets/lib/kookit-extra-browser.min";
import { getCache, setCache, hashText, getPageCache, setPageCache, TranslationItem } from "./cache";

const DEF_TRANS = "You are a literary translator. Translate the following English paragraph into natural Chinese. Only output the translation.";
const DEF_SUMM = "Summarize the following article in Chinese. Focus on core arguments, key evidence, and conclusions. Use bullet points. Output in Chinese.";

function getCfg() {
  const mk = ConfigService.getReaderConfig("paragraphTranslateModel") || ConfigService.getReaderConfig("aiTranslateModel") || "";
  const entry = mk ? ConfigService.getObjectConfig(mk, "aiModelConfig", null) : null;
  const cfg = entry?.config || {};
  return {
    apiKey: cfg.apiKey || "",
    endpoint: cfg.endpoint || "https://api.deepseek.com/v1",
    model: cfg.modelId || "deepseek-chat",
    transPrompt: ConfigService.getReaderConfig("paragraphTranslatePrompt") || ConfigService.getReaderConfig("aiTranslatePrompt") || DEF_TRANS,
    summPrompt: ConfigService.getReaderConfig("summaryPrompt") || DEF_SUMM,
  };
}

export async function translateParagraph(text: string, src = "en", tgt = "zh"): Promise<string> {
  if (!text?.trim()) return "";
  const cfg = getCfg();
  if (!cfg.apiKey) throw new Error("API key not configured");

  const hash = await hashText(text, src, tgt);
  const cached = await getCache(hash);
  if (cached) return cached.translatedText;

  let result = "";
  await chatStream(cfg.endpoint, "custom", cfg.apiKey, cfg.model, text,
    [{ role: "system", content: cfg.transPrompt.replace("{from}", src).replace("{to}", tgt) }],
    (r: { text: string }) => { result += r.text; });

  if (result) await setCache({ hash, originalText: text, translatedText: result, sourceLang: src, targetLang: tgt, createdAt: Date.now() });
  return result;
}

export async function summarizeText(fullText: string, onProgress?: (p: string) => void): Promise<string> {
  if (!fullText?.trim()) return "";
  const cfg = getCfg();
  if (!cfg.apiKey) throw new Error("API key not configured");
  const truncated = fullText.length > 30000 ? fullText.substring(0, 30000) + "\n\n[Truncated...]" : fullText;
  let result = "";
  await chatStream(cfg.endpoint, "custom", cfg.apiKey, cfg.model, truncated,
    [{ role: "system", content: cfg.summPrompt }],
    (r: { text: string }) => { result += r.text; onProgress?.(result); });
  return result;
}

export async function batchTranslate(texts: string[], src = "en", tgt = "zh", onProgress?: (d: number, t: number) => void): Promise<string[]> {
  const results: string[] = new Array(texts.length).fill("");
  let done = 0;
  const CONCURRENCY = 4;
  const translateOne = async (i: number) => {
    try { results[i] = await translateParagraph(texts[i], src, tgt); } catch { results[i] = ""; }
    done++;
    onProgress?.(done, texts.length);
  };
  for (let i = 0; i < texts.length; i += CONCURRENCY) {
    const batch = texts.slice(i, i + CONCURRENCY).map((_, j) => translateOne(i + j));
    await Promise.all(batch);
  }
  return results;
}

/**
 * 一页原始文本 → AI 分段 + 翻译 → 结构化 JSON。
 * 单次 API 调用完成两件事：
 *   1. AI 将 PDF 原始文本按语义切分为完整段落
 *   2. AI 翻译每个段落
 * 返回 [{ en: "完整段落原文", zh: "翻译" }, ...]
 * 结果缓存到磁盘（按 bookKey + pageIndex）
 */
export async function translatePageJson(
  bookKey: string,
  pageIndex: number,
  rawText: string,
  src = "en",
  tgt = "zh",
  force = false,
  onProgress?: (stage: string) => void
): Promise<TranslationItem[]> {
  if (!rawText.trim()) return [];
  const cfg = getCfg();
  if (!cfg.apiKey) throw new Error("API key not configured");

  // force=false 时检查磁盘缓存（预译等场景）；force=true 跳过缓存（用户点重新翻译）
  if (!force) {
    const cached = await getPageCache(bookKey, pageIndex);
    if (cached?.items?.length) return cached.items;
  }

  onProgress?.("translating");

  // 截断过长文本（~12000 字符 ≈ 单页 PDF 上限）
  const truncated = rawText.length > 15000 ? rawText.substring(0, 15000) : rawText;

  const systemPrompt = `You are an academic translator. Complete TWO tasks in order:

TASK 1 — SEGMENT the raw text into logical paragraphs:
- This text was extracted from a PDF with hard line breaks. Merge broken lines into flowing sentences.
- Discard ONLY: page numbers (like "42" or "Page 42"), repeated page headers (identical text on every page), and footnote markers.
- KEEP all section titles, subtitles, headings (like "Abstract", "Introduction", "2.1 Background"). Preserve them as separate paragraphs.
- Never truncate a sentence. Every sentence must appear complete in exactly one paragraph.
- Split where the topic changes naturally. Output 3–12 well-formed paragraphs in reading order.

TASK 2 — TRANSLATE each paragraph:
- Translate into natural, fluent Chinese. Use academic register. Keep technical terms accurate.
- Translate the FULL content — do NOT summarize or omit content.

OUTPUT — Return ONLY a JSON array with NO markdown fences:
[{"en": "complete original paragraph", "zh": "完整的中文翻译"}, ...]`;

  let result = "";
  await chatStream(cfg.endpoint, "custom", cfg.apiKey, cfg.model, truncated,
    [{ role: "system", content: systemPrompt }],
    (r: { text: string }) => { result += r.text; });

  // 解析 JSON 响应
  let items: TranslationItem[] = [];
  try {
    let cleaned = result.trim();
    cleaned = cleaned.replace(/^```(?:json)?\s*\n?/i, "").replace(/\n?\s*```$/, "");
    const start = cleaned.indexOf("[");
    const end = cleaned.lastIndexOf("]");
    if (start !== -1 && end !== -1 && end > start) {
      cleaned = cleaned.substring(start, end + 1);
    }
    const parsed = JSON.parse(cleaned);
    if (Array.isArray(parsed) && parsed.length > 0) {
      items = parsed
        .filter((item: any) => item?.en && item?.zh)
        .map((item: any) => ({ en: String(item.en).trim(), zh: String(item.zh).trim() }));
    }
  } catch (e) {
    console.error("translatePageJson: JSON 解析失败", e, "raw:", result.substring(0, 300));
    return [];
  }

  // 缓存
  if (items.length > 0) {
    await setPageCache({ bookKey, pageIndex, texts: [], items, createdAt: Date.now() });
  }

  return items;
}

export type { TranslationItem };
